import { Trans, useLingui } from "@lingui/react/macro";
import { UserRound } from "lucide-react";

import { usePendingOAuthLinkPrompt } from "@/components/auth/pending-oauth-link";
import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";

/**
 * "Connect this Facebook account to your PontiMed account?" — asked once a
 * session exists for a Facebook sign-in that was parked because its email
 * belongs to an existing account (components/auth/pending-oauth-link.ts).
 * The sheet shows the Facebook account's own name (2026-10-09 review): the
 * matched address is the person's OWN, so on its own it could not tell their
 * Facebook account from someone else's. Without a name (Facebook gave none)
 * it falls back to the address.
 * Only "Connect" adds Facebook as a sign-in method; "Not now", the X, the
 * backdrop or a swipe all discard the parked link.
 *
 * Mounted once, in `AuthModalProvider`.
 */
export function PendingOAuthLinkPrompt() {
  const { t } = useLingui();
  const {
    open,
    email: linkEmail,
    displayName,
    busy,
    connect,
    decline,
  } = usePendingOAuthLinkPrompt();
  // Named `email`: it is the `{email}` placeholder of the message below.
  const email = linkEmail ?? "";

  return (
    <ResponsiveSheet
      open={open}
      onClose={decline}
      dismissible={!busy}
      width="sm"
      title={<Trans>Connect Facebook?</Trans>}
      ariaLabel={t`Connect Facebook?`}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            className="min-h-[44px] w-full sm:w-auto"
            disabled={busy}
            onClick={decline}
          >
            <Trans>Not now</Trans>
          </Button>
          <Button className="min-h-[44px] w-full sm:w-auto" disabled={busy} onClick={connect}>
            <Trans>Connect</Trans>
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {displayName ? (
          <>
            <p className="text-foreground text-sm">
              <Trans>Connect this Facebook account to your PontiMed account?</Trans>
            </p>
            <div className="border-border bg-muted/50 flex min-h-[44px] items-center gap-3 rounded-lg border px-3 py-2.5">
              <UserRound className="text-muted-foreground size-4 shrink-0" aria-hidden />
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-medium">{displayName}</p>
                <p className="text-muted-foreground truncate text-xs">{email}</p>
              </div>
            </div>
          </>
        ) : (
          <p className="text-foreground text-sm break-words">
            {t`Connect the Facebook account ${email} to your PontiMed account?`}
          </p>
        )}
        <p className="text-muted-foreground text-xs">
          <Trans>
            Connect it only if this Facebook account is yours: you'll be able to sign in with it.
          </Trans>
        </p>
      </div>
    </ResponsiveSheet>
  );
}
